import { useState, useRef } from "react"
import type { TimeAdjustmentOptions } from "../../../services/time/timeService"
import { useSelector } from "react-redux"
import { Form, Formik } from "formik"
import { Alert, Badge, Button, Modal, Spinner } from "react-bootstrap"
import { Link } from "react-router-dom"
import { useBulkEditBookingsMutation, useEditBookingMutation, useGetBookingsQuery } from "../../../services/bClientApi"
import { accountSelector } from "../../../redux/account/accountSlice"
import { DateInputField } from "../../FormFields/DateInput/DateInputField"
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import timeService from "../../../services/time/timeService"
import * as yup from 'yup'
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook"
import useRelatedBookings from "../../../Hooks/UseRelatedBookings/UseRelatedBookings"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { isParent } from "../../../Helpers/Property/isParent"
import { currencyFormatter } from "../../../Helpers/Parsers/currencyFormatter"
import { getChildProperties } from "../../../Helpers/Property/multiUnit"
import { PropertyLinkedPropertyType } from "../../../Types/propertyTypes"

export default function ExtendStayModal(props: {
  active: boolean,
  booking: PropertyBookingEntry,
  clear: () => void,
}) {
  const { active, booking, clear } = props
  const [error, setError] = useState('')
  const [editBooking, editState] = useEditBookingMutation()
  const [bulkEditBookings, bulkEditState] = useBulkEditBookingsMutation()
  const { _id: currentUserId } = useSelector(accountSelector)
  const { myProperties } = useMyProperties()
  const relatedBookings = useRelatedBookings(booking?._id, booking, myProperties)

  const propertyData = myProperties[booking.propertyId]

  const checkOutHour = +propertyData?.about?.checkOutHour
  const checkOutMinute = +propertyData?.about?.checkOutMinute
  const propertyCheckOutTime: TimeAdjustmentOptions = {
    hour: Number.isFinite(checkOutHour) ? checkOutHour : 11,
    minute: Number.isFinite(checkOutMinute) ? checkOutMinute : 0,
    second: 0,
    millisecond: 0,
  }

  const propertyIds = [booking?.propertyId]
  const childProperties = getChildProperties(myProperties, booking?.propertyId)
  if (childProperties.length > 0) {
    propertyIds.push(...childProperties.map((child: PropertyLinkedPropertyType) => child.propertyId))
  }

  const {
    data: bookingData,
    isLoading: isLoadingAvailability,
    error: availabilityError,
  } = useGetBookingsQuery({
    bookingsQuery: {
      checkIn: booking?.checkOut,
      propertyId: propertyIds,
      bookingStatus: ['new', 'confirmed', 'pending', 'request'],
    },
    pagination: {
      page: 1,
      limit: 20,
      sort: { checkIn: 1 },
    },
  }, {
    skip: !active || !booking?._id,
  })

  const nextBooking = (bookingData?.docs || []).find((entry: any) => { return entry.sourceConfirmation !== booking?.sourceConfirmation })
  const currentCheckOut = booking?.checkOut
  const nextBookingCheckIn = nextBooking?.checkIn
  const nextAvailableDate = getNextAvailableDate(booking?.checkOut, propertyCheckOutTime, nextBooking)
  const noExtensionsAvailable = Boolean(currentCheckOut && nextBookingCheckIn && new Date(currentCheckOut).toISOString().split('T')[0] === new Date(nextBookingCheckIn).toISOString().split('T')[0])

  const loading = isLoadingAvailability || editState.isLoading || bulkEditState.isLoading

  const computedRef = useRef<any>(null)

  const handleCreate = async (values: { checkoutDate: Date | null, extensionEarnings: number, paymentSource: string }) => {
    const { checkoutDate, extensionEarnings, paymentSource } = values
    if (!booking?._id || !checkoutDate || extensionEarnings === undefined || !paymentSource) {
      return
    }

    setError('')

    const computed = computedRef.current ?? computeExtensionValues(booking, checkoutDate, extensionEarnings, propertyCheckOutTime)
    const { selectedDate } = computed

    try {
      const childBookings = relatedBookings.filter(relatedBooking => (
        !isParent(myProperties[relatedBooking.propertyId], myProperties[booking.propertyId])
      ))

      if (childBookings.length > 0) {
        const childExtensionEarnings = +extensionEarnings / childBookings.length
        const bookingsToEdit = [createExtensionUpdate(booking, +extensionEarnings, selectedDate, paymentSource, currentUserId)]
        childBookings.forEach(childBooking => {
          bookingsToEdit.push(createExtensionUpdate(childBooking, childExtensionEarnings, selectedDate, paymentSource, currentUserId))
        })
        await bulkEditBookings(bookingsToEdit).unwrap()
      } else {
        await editBooking({
          bookingId: booking._id,
          bookingInfo: createExtensionUpdate(booking, +extensionEarnings, selectedDate, paymentSource, currentUserId),
        }).unwrap()
      }
      clear()
    } catch (requestError: any) {
      setError(requestError?.data?.error || 'Unable to create extension')
    }
  }

  return (
    <Modal show={active} onHide={clear}>
      <Modal.Header closeButton>
        <Modal.Title>Extend Stay</Modal.Title>
      </Modal.Header>
      <Formik
        key={`${active}-${booking?._id || ''}`}
        initialValues={{ checkoutDate: null, extensionEarnings: 0, paymentSource: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleCreate(values)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => {

          const {
            checkoutDate,
            extensionEarnings,
          } = values

          const {
            selectedDate,
            additionalNights,
            newTotalNights,
            newEarnings,
            newPricePerNight,
            minDate
          } = computeExtensionValues(booking, checkoutDate, extensionEarnings, propertyCheckOutTime)

          computedRef.current = { selectedDate, additionalNights, newTotalNights, newEarnings, newPricePerNight, minDate }

          return (
            <Form>
              <Modal.Body>
                {loading && (
                  <div className="text-center"><Spinner animation="border" size="sm" /></div>
                )}
                {(error || availabilityError) && (
                  <Alert variant="danger">{error || 'Unable to check availability'}</Alert>
                )}
                {noExtensionsAvailable && (
                  <Alert variant="warning">
                    Extensions are not available for this booking.
                  </Alert>
                )}
                {!noExtensionsAvailable && (
                  <>
                    {relatedBookings.length > 0 && (
                      <div className="related-bookings mt-2 mb-4">
                        <b>Related Bookings</b>
                        <div>
                          {relatedBookings.map((relatedBooking: PropertyBookingEntry) => {
                            const propertyInfo = myProperties[relatedBooking.propertyId]
                            const duplicateRelated = relatedBooking.propertyId === booking.propertyId
                            return (
                              <div key={relatedBooking._id}>
                                <Link to={`/booking/${relatedBooking._id}`}>
                                  {propertyInfo?.title || relatedBooking.propertyId}
                                </Link>{' '}
                                {isParent(myProperties[relatedBooking.propertyId], myProperties[booking.propertyId]) && (
                                  <span className="parent-property">- Parent (edit this to keep changes synced)&nbsp;</span>
                                )}
                                {duplicateRelated && <Badge bg="danger">DUPLICATE</Badge>}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    <p className="mb-3">
                      Original earnings: <strong>{currencyFormatter(booking.earnings || 0)}</strong><br />
                      Original price per night: <strong>{currencyFormatter(booking.pricePerNight || 0)}</strong><br />
                      Current checkout: <strong>{timeService.getFormattedTime(currentCheckOut)}</strong><br />
                      Available through: <strong>{timeService.getFormattedTime(nextAvailableDate)}</strong>
                    </p>
                    <DateInputField
                      fieldName="checkoutDate"
                      fieldLabel="New checkout date"
                      customClass="mb-3"
                      minDate={timeService.getReverseOffsetJSDate(minDate)}
                      maxDate={nextAvailableDate}
                      extraOptions={{
                        showTimeSelect: false,
                        dateFormat: 'yyyy-MM-dd',
                        disabled: loading,
                      }}
                      onChange={(date: Date | null) => {
                        if (!date) return
                        const nights = timeService.calculateNights(currentCheckOut, date)
                        setFieldValue('extensionEarnings', nights > 0
                          ? (+(booking.pricePerNight || 0) * nights).toFixed(2)
                          : null)
                      }}
                    />
                    <SelectInputField
                      fieldName="paymentSource"
                      fieldLabel="Payment source"
                      placeholder="Select payment source"
                      required
                      disabled={loading}
                      options={PAYMENT_SOURCE_OPTIONS}
                    />
                    <TextInputField
                      fieldLabel="Extension earnings"
                      fieldName="extensionEarnings"
                      placeholder=""
                      fieldType="number"
                      required
                      disabled={loading}
                    />
                    {checkoutDate && additionalNights > 0 && (
                      <Alert variant="info" className="mb-0">
                        {additionalNights} additional night{additionalNights === 1 ? '' : 's'} at {currencyFormatter(booking.pricePerNight)} per night:&nbsp;
                        <strong>{currencyFormatter(extensionEarnings)}</strong><br />
                        New price per night: <strong>{currencyFormatter(newPricePerNight)}</strong><br />
                        New earnings: <strong>{currencyFormatter(newEarnings)}</strong>
                      </Alert>
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={clear}>Cancel</Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || isSubmitting || noExtensionsAvailable || Object.keys(errors).length > 0 || additionalNights <= 0}
                >
                  {editState.isLoading ? 'Adding...' : 'Add Extension'}
                </Button>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export const PAYMENT_SOURCE_OPTIONS = [
  { value: 'airbnb', text: 'Airbnb'},
  { value: 'stripe', text: 'Stripe' },
  { value: 'zelle', text: 'Zelle' },
  { value: 'wire', text: 'Wire' },
  { value: 'paypal', text: 'PayPal' },
  { value: 'vrbo', text: 'Vrbo'},
  { value: 'cash', text: 'Cash' },
]

const validationSchema = yup.object().shape({
  checkoutDate: yup.date().nullable().required('Checkout date is required'),
  // should be greater than or equal to zero
  extensionEarnings: yup.number()
    .required('Extension earnings is required')
    .min(0, 'Extension earnings must be greater than or equal to zero'),
  paymentSource: yup.string().required('Payment source is required'),
})


const getNextAvailableDate = (checkOut: Date | string | number, propertyCheckOutTime: TimeAdjustmentOptions, nextBooking?: any) => {
  const checkoutDateTime = timeService.createLuxonDate(checkOut)
  const nextBookingCheckIn = nextBooking?.checkIn ? timeService.createLuxonDate(nextBooking.checkIn) : null

  if (!checkoutDateTime) return 0
  if (nextBookingCheckIn) {
    return nextBookingCheckIn.set(propertyCheckOutTime).toMillis()
  }

  return checkoutDateTime.plus({ months: 6 }).set(propertyCheckOutTime).toMillis()
}

const computeExtensionValues = (
  booking: PropertyBookingEntry,
  checkoutDate: Date | null,
  extensionEarningsInput: string | number,
  propertyCheckOutTime: TimeAdjustmentOptions
) => {
  const currentCheckIn = booking?.checkIn
  const currentCheckOut = booking?.checkOut
  const minDate = new Date(currentCheckOut)
  minDate.setUTCDate(minDate.getUTCDate() + 1)
  if (!checkoutDate) {
    return {
      selectedDate: null,
      additionalNights: 0,
      newTotalNights: 0,
      newEarnings: +(booking.earnings || 0),
      newPricePerNight: +(booking.pricePerNight || 0).toFixed(2),
      minDate: minDate
    }
  }
  const selectedDate = timeService.setDateTimeWithTimezone(checkoutDate, propertyCheckOutTime)
  const additionalNights = selectedDate ? timeService.calculateNights(currentCheckOut, selectedDate) : 0
  const newTotalNights = selectedDate ? timeService.calculateNights(currentCheckIn, selectedDate) : 0
  const newEarnings = +(booking.earnings || 0) + +(extensionEarningsInput || 0)
  const newPricePerNight = newTotalNights > 0 ? +(newEarnings / newTotalNights).toFixed(2) : +(booking.pricePerNight || 0).toFixed(2)

  return { selectedDate, additionalNights, newTotalNights, newEarnings, newPricePerNight, minDate }
}


const createExtensionUpdate = (
  targetBooking: any,
  targetExtensionEarnings: number,
  selectedDate: Date | null,
  paymentSource: string,
  currentUserId: string = 'unknown'
) => {
  const targetTotalNights = timeService.calculateNights(targetBooking.checkIn, selectedDate)
  const targetEarnings = +(targetBooking.earnings || 0) + targetExtensionEarnings
  const targetPricePerNight = targetTotalNights > 0 ? +(targetEarnings / targetTotalNights).toFixed(2) : +(targetBooking.pricePerNight || 0).toFixed(2)
  const confirmedAt = Date.now()
  const productId = `extend-stay-${targetBooking._id}`
  const linkedServices = [...(targetBooking.linkedServices || [])]

  linkedServices.push({
    linkedService: `${productId}-confirmed-${confirmedAt}`,
    linkedServiceId: JSON.stringify({
      newCheckOut: selectedDate?.getTime(),
      newEarnings: targetEarnings,
      newPricePerNight: targetPricePerNight,
      originalCheckOut: timeService.dateToMillis(targetBooking.checkOut),
      originalEarnings: +(targetBooking.earnings || 0),
      originalPricePerNight: +(targetBooking.pricePerNight || 0),
      extensionEarnings: targetExtensionEarnings,
      confirmed: confirmedAt,
      paymentSource,
      createdBy: currentUserId,
    }),
    linkedServicePrice: targetExtensionEarnings,
  })

  return {
    ...targetBooking,
    checkOut: selectedDate?.getTime(),
    earnings: targetEarnings,
    pricePerNight: targetPricePerNight,
    linkedServices,
  }
}

